/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService, BuilderUserContext } from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermission,
  EDocType,
  getAelEventsArray,
  QuotationBase,
  SalesFolderStatus,
} from '@gh/builder-shared-data';
import { rendererIconByName, truthyCellRenderer } from '@gh/common-internal-shared';
import {
  AppRouter,
  DateTimeTypes,
  EnumDictionary,
  getRouteInitData,
  getRouteParam,
  NumberTypes,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { Enum } from '@gh/core-data';
import { catchErrors, mapMessageParams } from '@gh/core-messages';
import {
  DialogService,
  NotificationService,
  STYLES_FLEX_COLUMN_LAYOUT,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { throwError$ } from '@gh/rx';
import { filter$, switchMap$ } from '@gh/rx/operators';

import { assign } from 'lodash';

import { quoteCloneDataDialogRoute, QuoteCloneDialogEntryPoint } from '../../../';
import { RfqBusinessService } from '../../../resources/rfq-business.service';
import { RfqQuotation, RfqQuotationsGetResponse } from '../../../resources/rfq.model';
import { RfqTabContainerComponent } from '../rfq-tab-container.component';

@Component({
  standalone: false,
  selector: 'gh-lead-details-rfq-quotation-tab',
  templateUrl: 'rfq-quotation-list-tab.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class RfqQuotationListTabComponent implements OnInit {
  gridDefinition: LayoutBasedGridDefinition;
  quotationsItem: RfqQuotationsGetResponse;
  quotationBases: EnumDictionary<QuotationBase>;
  edocTypeDict: EnumDictionary<EDocType>;
  isEditMode: boolean;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private gridDefinitionFactory: GridDefinitionFactory,
              private navigations: BuilderNavigationService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private parentContainer: RfqTabContainerComponent,
              private waitings: WaitingService,
              private business: RfqBusinessService,
              private userContext: BuilderUserContext) {

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      date: {
        ...buildDateTimeColumn(DateTimeTypes.DateTime),
        sort: 'desc',
      },
      isApproved: {
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
      },
      rfqPrice1: buildNumberColumn(NumberTypes.Price),
      base: {
        valueFormatter: (params: any) => params.node.data.base ? this.quotationBases.get(params.node.data.base) : '',
      },
    },                                                                {
      getRowId: (params) => String(params.data.id)
    });
  }

  canSendToSign(quotation: RfqQuotation): boolean {
    return isNotNil(quotation.rfqNumber);
  }

  ngOnInit(): void {
    const { gridLayout, quotationsItem, quotationBases, edocTypeDict } = getRouteInitData(this.route);

    this.isEditMode = (this.parentContainer.salesFolder.status === SalesFolderStatus.Active) &&
      this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT);

    assign(this, { quotationsItem, quotationBases, edocTypeDict });

    this.gridDefinition.bind(gridLayout);

  }

  goToAel(quotationId: ID): void {
    this.navigations.goToAel({
      label: 'lbl_quotations_list',
      entityType: quotationId ? 'QUOTATION' : 'SALES_FOLDER',
      entityId: quotationId ? quotationId : this.quotationsItem.salesFolderId,
      eventTypeIds: quotationId ? void 0 : getAelEventsArray(AelEvents.SalesFolders.Quotation),
    });
  }

  goToRfqAel(): void {
    this.navigations.goToAel({
      label: 'lbl_quotations_list',
      entityId: this.quotationsItem.salesFolderId,
      entityType: 'SALES_FOLDER',
    });
  }

  onOpen(quotation: RfqQuotation): void {
    this.router.navigateToChild(['/sales/sales-folders', this.getSalesFolderId(), 'rfq', 'quotations', quotation.id]);
  }

  onCreate(): void {
    this.router.navigateToChild(['/sales/sales-folders', this.getSalesFolderId(), 'rfq', 'quotations', 'new']);
  }

  onClone(): void {
    this.dialogs.openWithInput(quoteCloneDataDialogRoute, {
      contactId: this.quotationsItem.contactId,
      customerName: this.quotationsItem.customerName,
      entryPoint: QuoteCloneDialogEntryPoint.CloneQuote,
      salesFolderId: this.getSalesFolderId(),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe(({ quotationId }) =>
        this.router.navigateToChild([
          '/sales/sales-folders',
          this.getSalesFolderId(),
          'rfq',
          'quotations',
          quotationId,
        ]));
  }

  onSendToSign(quotation: RfqQuotation): void {
    const sfId = this.getSalesFolderId();
    // send documents to sign
    this.business.sendQuoteToSign(sfId, quotation.id, {}).pipe(
      this.waitings.operator(),
      (catchErrors(
        [
          'msg_e_document_signed',
          (basket) => this.dialogs.confirmation(yesNoConfirmation(
            'msg_e_document_signed',
            this.translateMessageParams(basket.get('msg_e_document_signed')!.params))).pipe(
            filter$(yesAnswer),
            // if user wants to sign new documents => run sign process again without duplicate check
            switchMap$(() =>
              this.business.sendQuoteToSign(sfId, quotation.id, { skipDuplicateSendCheck: true }).pipe(
                this.waitings.operator()))),
        ],
        [
          'msg_e_document_pending_signature',
          (basket) => throwError$(basket.map((error) => mapMessageParams(
            error,
            (params) => this.translateMessageParams(params)))),
        ],
      )))
      .subscribe(
        () => this.notifications.success('msg_e_doc_added_successfully'),
        this.notifications.processAll);
  }

  private getSalesFolderId(): ID {
    return getRouteParam(this.route.parent!, 'salesFolderId');
  }

  private translateMessageParams(params: Hash<any>): Hash<any> {
    return {
      ...params,
      types: params.types
        ? params.types.map((type: string) => this.edocTypeDict.get(Enum.byValue(EDocType, type))).join(', ')
        : void 0,
    };
  }
}
